import type { CompletionItem } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, patch, post } from "../api";
import { Alert, Badge, Button, Empty, Field, Loading, Panel, PanelHeader, TextAreaField, formatCents, formatDateTime } from "../components/ui";
import { useSession } from "../session";

/** On an insurance job's Invoices tab: the certificate of completion and the carrier's summary. */
export function JobCompletion({ jobId }: { jobId: string }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["completion", jobId], queryFn: () => get<CompletionItem[]>(`/api/jobs/${jobId}/completion`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["completion", jobId] });
  const start = useMutation({ mutationFn: () => post<CompletionItem>(`/api/jobs/${jobId}/completion`), onSuccess: refresh });
  const current = q.data?.[0];
  return (
    <Panel>
      <PanelHeader
        title="Completion package"
        description="When the work is done: issue our certificate of completion and the final invoice summary, and email them to the carrier to release depreciation."
        action={
          can("proposals.edit") &&
          (!current || current.status === "issued") && (
            <Button variant="primary" className="px-3 py-1.5" busy={start.isPending} onClick={() => start.mutate()}>
              Start completion package
            </Button>
          )
        }
      />
      {start.error && (
        <div className="px-5 pt-4">
          <Alert>{errorMessage(start.error)}</Alert>
        </div>
      )}
      {q.isLoading ? (
        <div className="px-5">
          <Loading />
        </div>
      ) : !current ? (
        <Empty>Not started.</Empty>
      ) : (
        <Package key={`${current.id}-${current.status}`} c={current} onChange={refresh} />
      )}
    </Panel>
  );
}

function Package({ c, onChange }: { c: CompletionItem; onChange: () => void }) {
  const { can } = useSession();
  const edit = can("proposals.edit");
  const [completedOn, setCompletedOn] = useState(c.completedOn);
  const [carrierEmail, setCarrierEmail] = useState(c.carrierEmail);
  const [note, setNote] = useState(c.note);
  const [copied, setCopied] = useState(false);
  const save = useMutation({ mutationFn: () => patch<CompletionItem>(`/api/completion/${c.id}`, { completedOn, carrierEmail, note }), onSuccess: onChange });
  const issue = useMutation({
    mutationFn: async () => {
      await patch(`/api/completion/${c.id}`, { completedOn, carrierEmail, note });
      return post<CompletionItem>(`/api/completion/${c.id}/issue`);
    },
    onSuccess: onChange,
  });
  const received = useMutation({ mutationFn: () => post<CompletionItem>(`/api/completion/${c.id}/depreciation-received`), onSuccess: onChange });
  const s = c.summary;
  const err = save.error ?? issue.error ?? received.error;
  const mailto = `mailto:${encodeURIComponent(c.carrierEmail)}?subject=${encodeURIComponent(c.email.subject)}&body=${encodeURIComponent(c.email.body)}`;
  const row = (label: string, cents: number, strong = false) => (
    <div className={strong ? "flex justify-between border-t border-mist py-1 font-semibold" : "flex justify-between py-0.5"}>
      <span>{label}</span>
      <span>{formatCents(cents)}</span>
    </div>
  );
  return (
    <div className="grid gap-5 px-5 py-5 lg:grid-cols-2">
      <div className="flex flex-col gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={c.status === "issued" ? "ok" : "wait"}>{c.status === "issued" ? "Issued" : "Draft"}</Badge>
          {c.issuedAt && <span className="text-muted">Issued by {c.issuedBy ?? "NMT"} {formatDateTime(c.issuedAt)}</span>}
        </div>
        <div className="rounded-[var(--radius-control)] bg-mist px-3 py-2">
          <div className="mb-1 text-xs font-semibold tracking-wide text-muted uppercase">Work completed</div>
          {row("Contract work (RCV)", s.contractRcvCents)}
          {s.supplementsCents > 0 && row("Approved supplements", s.supplementsCents)}
          {row("Total insured work", s.totalCompletedCents, true)}
          <div className="mt-2 mb-1 text-xs font-semibold tracking-wide text-muted uppercase">Requested from the carrier</div>
          {s.recoverableCents > 0 && row("Recoverable depreciation", s.recoverableCents)}
          {s.codeUpgradeCents > 0 && row("Code upgrades incurred", s.codeUpgradeCents)}
          {s.supplementsCents > 0 && row("Supplements", s.supplementsCents)}
          {row("Amount requested", s.requestedCents, true)}
        </div>
        <div className="flex flex-wrap gap-3">
          <a className="font-semibold text-brand hover:underline" href={c.summaryUrl ?? `/api/completion/${c.id}/summary.pdf`} target="_blank" rel="noreferrer">
            Invoice summary (PDF)
          </a>
          <a className="font-semibold text-brand hover:underline" href={c.certificateUrl ?? `/api/completion/${c.id}/certificate.pdf`} target="_blank" rel="noreferrer">
            {c.certificateUrl ? "Certificate of completion (PDF)" : "Certificate preview (PDF)"}
          </a>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {err && <Alert>{errorMessage(err)}</Alert>}
        <Field label="Work completed on" type="date" value={completedOn} disabled={!edit || c.status !== "draft"} onChange={(e) => setCompletedOn(e.target.value)} />
        <Field label="Carrier claims email" type="email" value={carrierEmail} disabled={!edit || c.status === "issued"} onChange={(e) => setCarrierEmail(e.target.value)} placeholder="claims@carrier.com" />
        {c.status !== "issued" && <TextAreaField label="Note (for us)" rows={2} value={note} disabled={!edit} onChange={(e) => setNote(e.target.value)} />}
        {edit && c.status !== "issued" && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2">
              <Button busy={save.isPending} onClick={() => save.mutate()}>
                Save
              </Button>
              <Button variant="primary" busy={issue.isPending} onClick={() => window.confirm("Issue the certificate of completion and the invoice summary? The numbers are final after this.") && issue.mutate()}>
                Issue certificate and summary
              </Button>
            </div>
            <p className="text-xs text-muted">Issuing saves both PDFs to the job's files, with you named on the certificate for NMT. The homeowner doesn't sign anything.</p>
          </div>
        )}
        {c.status === "issued" && (
          <>
            <p className="text-sm text-ink-soft">Email the certificate and the invoice summary to the carrier. The CRM doesn't send email yet: open the email below, attach both PDFs, and send.</p>
            <div className="flex flex-wrap gap-2">
              <a href={mailto} className="inline-flex items-center rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep">
                Open the email
              </a>
              <Button
                onClick={async () => {
                  await navigator.clipboard.writeText(`${c.email.subject}\n\n${c.email.body}`);
                  setCopied(true);
                }}
              >
                {copied ? "Copied" : "Copy the email"}
              </Button>
            </div>
            {edit && (
              <Button variant={c.depreciationReceivedAt ? "quiet" : "secondary"} busy={received.isPending} onClick={() => received.mutate()}>
                {c.depreciationReceivedAt ? `Depreciation received ${formatDateTime(c.depreciationReceivedAt)} (undo)` : "Mark depreciation received"}
              </Button>
            )}
            {!c.depreciationReceivedAt && <p className="text-xs text-muted">You'll get a reminder task 30 days after issuing, and every two weeks after, until it's marked received.</p>}
          </>
        )}
      </div>
    </div>
  );
}
